import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  getFileForSourceDocument,
  useExtractDocs,
} from "context/ExtractDocsContext";
import {
  CONTRADICTION_STATUS,
  QUERY_PROGRESS_NOT_STARTED,
  RECARE_FILE_TYPE,
  SOURCE_DOCUMENT_SOURCES,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getStaticAsset } from "core/model/config";
import { isFileHISStateful, isFileV2 } from "core/model/files/utils";
import { formatUnixDate } from "core/model/utils/dates";
import { activateDocsConflictReasoning } from "core/model/utils/featureFlags";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  Contradictions,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormSourceDocument,
  ExtractOCRStatusResult,
  FileState,
  Patient,
  QueryProgress,
  ValueOf,
} from "core/types";
import {
  Banner,
  Button,
  Card,
  Chip,
  ChipProps,
  Image,
  Select,
  Spinner,
  Tabs,
  TabsProps,
} from "ds/ui";
import { DocxViewer } from "dsl/atoms/DocxViewer";
import { PdfViewer } from "dsl/atoms/PDFViewer";
import { TruncationTooltip } from "dsl/ecosystems/DoctorDashboard/DoctorDashboardTable";
import { useCanExtractAllFiles } from "dsl/ecosystems/PatientFiles";
import {
  isDocxFileName,
  isFileFormUploaded,
} from "dsl/ecosystems/PatientFiles/utils";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckIcon,
  CircleAlertIcon,
  FileSearch2Icon,
  FileTextIcon,
  ListIcon,
  MicIcon,
  ShieldAlertIcon,
  SparklesIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useLocale, useTranslations } from "translations";
import Translations from "translations/types";
import { SourceSelected } from "../useDocsForm";
import { useSelectSourceDocumentFile } from "../utils";
import { KisJsonViewer } from "./KisJsonViewer";
import {
  ContradictionValueToHumanReadable,
  FORM_NAME_TO_CONTRADICTION_VALUE_CONFIG,
  useFieldLabel,
} from "./utils";

export const getDocumentTabKey = (doc: DocsFormSourceDocument): string => {
  if (doc.source === SOURCE_DOCUMENT_SOURCES.RECARE_DATA) {
    return String(SOURCE_DOCUMENT_SOURCES.RECARE_DATA);
  }

  if (doc.source === SOURCE_DOCUMENT_SOURCES.RECARE_FILE) {
    return String(doc.recare_file_id);
  }

  if (doc.source === SOURCE_DOCUMENT_SOURCES.KIS_FILE) {
    return String(doc.kis_file_id);
  }

  return String(doc.id);
};

// Tab order: recare_file → recare_data → kis_file → kis_data.
const SOURCE_DOCUMENT_TAB_ORDER_RANK: Record<
  DocsFormSourceDocument["source"],
  number
> = {
  [SOURCE_DOCUMENT_SOURCES.KIS_DATA]: 3,
  [SOURCE_DOCUMENT_SOURCES.KIS_FILE]: 2,
  [SOURCE_DOCUMENT_SOURCES.RECARE_DATA]: 1,
  [SOURCE_DOCUMENT_SOURCES.RECARE_FILE]: 0,
};

const getSourceDocumentTabOrderRank = (
  source: DocsFormSourceDocument["source"],
): number => SOURCE_DOCUMENT_TAB_ORDER_RANK[source] ?? 99;

function sortSourceDocumentsForTabs(
  documents: DocsFormSourceDocument[],
): DocsFormSourceDocument[] {
  const cloned = documents.slice();
  cloned.sort((a, b) => {
    const rank =
      getSourceDocumentTabOrderRank(a.source) -
      getSourceDocumentTabOrderRank(b.source);
    if (rank !== 0) {
      return rank;
    }
    return getDocumentTabKey(a).localeCompare(getDocumentTabKey(b));
  });
  return cloned;
}

export const isContradictingFile = (
  contradictions?: Contradictions,
  tabKey?: string,
): boolean => {
  if (
    !contradictions ||
    !tabKey ||
    contradictions.status !== CONTRADICTION_STATUS.CONFLICT
  ) {
    return false;
  }
  return contradictions.candidates.some((candidate) =>
    candidate.source_documents.some((doc) => getDocumentTabKey(doc) === tabKey),
  );
};

const ConflictReasoningBanner = ({
  contradictions,
  fieldName,
}: {
  contradictions: Contradictions | undefined;
  fieldName: string | undefined;
}) => {
  const translations = useTranslations();

  if (contradictions?.status !== CONTRADICTION_STATUS.CONFLICT) {
    return null;
  }

  const reasoning = contradictions.reasoning;

  if (!activateDocsConflictReasoning || !reasoning) {
    return (
      <Banner
        key={fieldName}
        color="danger"
        message={
          translations.extractDocs.splitView.sourcesTab
            .bannerContradictingInformation
        }
        withClose
      />
    );
  }

  return (
    <Banner
      key={fieldName}
      color="primary"
      icon={SparklesIcon}
      message={reasoning}
      withClose
    />
  );
};

export const ITEM_KIND = {
  AI_CORE: "ai-core",
  KIS_FILE: "kis-file",
  RECARE_FILE: "matching-file",
} as const;

export type AiCoreItem = {
  kind: typeof ITEM_KIND.AI_CORE;
  doc: Pick<
    ExtractOCRStatusResult,
    | "document_id"
    | "filename"
    | "source"
    | "uploaded_at"
    | "source_document_uri"
  >;
};
export type FileItem = {
  kind: typeof ITEM_KIND.KIS_FILE | typeof ITEM_KIND.RECARE_FILE;
  name: string;
  file: FileState;
};

export type SourceListItem = AiCoreItem | FileItem;

const isAiCoreItem = (item: SourceListItem): item is AiCoreItem => {
  return item.kind === ITEM_KIND.AI_CORE;
};

const isFileItem = (item: SourceListItem): item is FileItem => {
  return (
    item.kind === ITEM_KIND.KIS_FILE || item.kind === ITEM_KIND.RECARE_FILE
  );
};

export function getSourceListItemTabKey(item: SourceListItem): string {
  return isAiCoreItem(item) ? item.doc.document_id : String(item.file.id);
}

function getUploadedAt(item: SourceListItem): number {
  return isAiCoreItem(item)
    ? item.doc.uploaded_at ?? 0
    : item.file.updated_at ?? 0;
}

export function getManuallyUploadedFileAccountName(file: FileState): string {
  if (isFileHISStateful(file) || !isFileV2(file)) {
    return "";
  }

  if (!file.account?.first_name && !file.account?.last_name) {
    return "";
  }

  return `${file.account.first_name ?? ""} ${
    file.account.last_name ?? ""
  }`.trim();
}

function mergeAndSortSourceListItems(
  files: { name: string; file: FileState }[],
  kisDataDocuments: ExtractOCRStatusResult[],
): SourceListItem[] {
  const fileItems: SourceListItem[] = files.map(({ file, name }) => ({
    file,
    kind: isFileV2(file) ? ITEM_KIND.RECARE_FILE : ITEM_KIND.KIS_FILE,
    name,
  }));

  const aiCoreItems: SourceListItem[] = kisDataDocuments.map((doc) => ({
    doc,
    kind: ITEM_KIND.AI_CORE,
  }));

  return [...fileItems, ...aiCoreItems].sort(
    (a, b) => getUploadedAt(b) - getUploadedAt(a),
  );
}

export const ContentHeaderBanners = ({
  selectedSource,
}: {
  selectedSource: SourceSelected<DocsFormName> | undefined;
}) => {
  const translations = useTranslations();

  if (
    selectedSource?.contradictions?.status === CONTRADICTION_STATUS.CONFLICT
  ) {
    return (
      <div className="flex w-full flex-col gap-2">
        <ConflictReasoningBanner
          contradictions={selectedSource?.contradictions}
          fieldName={selectedSource?.fieldName}
        />
      </div>
    );
  }

  if (selectedSource?.sourceFields.manually_edited) {
    return (
      <Banner
        key={selectedSource?.fieldName}
        color="primary"
        message={
          translations.extractDocs.splitView.sourcesTab.fieldEditedManually
        }
        withClose
      />
    );
  }

  return (
    <Banner
      key={selectedSource?.fieldName}
      color="primary"
      message={
        translations.extractDocs.splitView.sourcesTab
          .multipleSourcesUsedInformation
      }
      withClose
    />
  );
};

const getTabTitle = ({
  contradictions,
  file,
  index,
  tabDocument,
  translations,
}: {
  contradictions: Contradictions | undefined;
  file: boolean;
  tabDocument: DocsFormSourceDocument;
  translations: Translations;
  index: number;
}) => {
  const label =
    tabDocument.source === SOURCE_DOCUMENT_SOURCES.RECARE_DATA
      ? translations.extractDocs.splitView.sourcesTab.tabPatientProfile
      : tabDocument.source === SOURCE_DOCUMENT_SOURCES.KIS_DATA
      ? translations.extractDocs.splitView.sourcesTab.tabHis
      : tabDocument.filename?.replace(/\.[^.]+$/, "") ||
        translations.extractDocs.splitView.sourcesTab.tabTitle({
          count: String(index + 1),
        });

  if (tabDocument.is_deleted && !file) {
    return (
      <div className="flex min-w-0 items-center gap-1">
        <ShieldAlertIcon className="shrink-0 text-warning-dark" size={16} />
        <span className="truncate">{label}</span>
      </div>
    );
  }
  if (isContradictingFile(contradictions, getDocumentTabKey(tabDocument))) {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <AlertTriangleIcon className="shrink-0 text-danger-dark" size={16} />
        <span className="truncate">{label}</span>
      </div>
    );
  }
  return label;
};

const Header = ({
  onSelectionChange,
  selectedDocument,
  selectedSource,
  tabItems,
}: {
  tabItems: (TabsProps["items"][number] & { titleText?: string })[];
  selectedDocument: DocsFormSourceDocument | undefined;
  onSelectionChange: (fileId: string) => void;
  selectedSource: SourceSelected<DocsFormName> | undefined;
}) => {
  const translations = useTranslations();

  if (!tabItems.length || !selectedDocument) {
    return null;
  }

  const selectedKey = getDocumentTabKey(selectedDocument);
  const selectedContent = tabItems.find((item) => item.key === selectedKey)
    ?.content;

  if (tabItems.length === 1) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col">
        {tabItems[0].content}
      </div>
    );
  }

  const selectItems = tabItems.map((item) => ({
    id: item.key,
    label: item.title,
  }));
  const firstKey = tabItems[0]?.key;
  const selectedTitleText =
    tabItems.find((i) => i.key === selectedKey)?.titleText ??
    selectedKey ??
    null;

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <ContentHeaderBanners selectedSource={selectedSource} />
      <div className="flex w-full flex-nowrap items-center gap-4">
        {tabItems.length >= 5 ? (
          <div className="flex flex-1 flex-row items-center gap-3">
            <p className="text-body-md font-semibold whitespace-nowrap">
              {translations.patientForms.sourceSelectLabel}
            </p>
            <div className="w-full max-w-[183px]">
              <Select
                aria-label={translations.patientForms.sourceSelectLabel}
                items={selectItems}
                renderItem={(item) => item.label}
                renderValue={() =>
                  selectedTitleText && (
                    <TruncationTooltip
                      className="w-full"
                      content={selectedTitleText}
                    >
                      <span className="block truncate">
                        {selectedTitleText}
                      </span>
                    </TruncationTooltip>
                  )
                }
                selectedKeys={[selectedKey ?? firstKey].filter(Boolean)}
                onSelectionChange={(keys) => {
                  const [key] = [...keys];
                  onSelectionChange(String(key));
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden">
            <Tabs
              aria-label={translations.extractDocs.splitView.sourcesTab.title}
              classNames={{
                base: "w-full overflow-hidden",
                panel: "!hidden",
                tab: "flex-1 min-w-0 overflow-hidden",
                tabContent: "truncate",
                tabList: "w-full",
              }}
              items={tabItems}
              selectedKey={selectedKey}
              variant="underlined"
              onSelectionChange={(key) => onSelectionChange(key.toString())}
            />
            <hr className="w-full border-gray-400" />
          </div>
        )}
      </div>
      {selectedContent != null ? (
        <div className="flex min-h-0 w-full flex-1">{selectedContent}</div>
      ) : null}
    </div>
  );
};

const ExtractionStatusChip = ({ file }: { file: FileState }) => {
  const translations = useTranslations();
  const { utils } = useExtractDocs();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  const extractionStatus: (ChipProps & { label: string }) | null =
    useMemo(() => {
      if (utils.isFileExtractionFailed(file)) {
        return {
          color: "danger",
          id: "extraction-failed-chip",
          label: translations.extractDocs.extractBanner.extractionError,
          startContent: <AlertTriangleIcon size={16} />,
        };
      }
      if (utils.isFileExtractionProcessing(file)) {
        return {
          color: "primary",
          id: "extraction-processing-chip",
          label: translations.extractDocs.filesTabChanges.buttonExtracting,
          startContent: (
            <Spinner
              color="primary"
              size="xs"
              testId="spinner-extraction-processing"
            />
          ),
        };
      }
      if (utils.isFileExtracted(file)) {
        return {
          color: "success",
          id: "extracted-chip",
          label: translations.extractDocs.filesTabChanges.buttonExtracted,
          startContent: <CheckIcon size={16} />,
        };
      }

      if (isDocsAndExtractActive || isDischargeLetterActive) {
        return {
          color: "warning",
          id: "not-extracted-chip",
          label: translations.extractDocs.filesTabChanges.chipNotExtracted,
          startContent: <CircleAlertIcon size={16} />,
        };
      }
      return null;
    }, [
      file.id,
      translations,
      isDischargeLetterActive,
      isDocsAndExtractActive,
      utils,
    ]);

  if (extractionStatus === null) {
    return null;
  }

  return (
    <Chip
      color={extractionStatus.color}
      id={extractionStatus.id}
      size="sm"
      startContent={extractionStatus.startContent}
      variant="flat"
    >
      {extractionStatus.label}
    </Chip>
  );
};

const SourceItemCardBody = ({
  isLoading = false,
  item,
}: {
  item: SourceListItem;
  isLoading?: boolean;
}) => {
  const translations = useTranslations();
  const locale = useLocale();

  // TODO: this will be an AI Core item in the future
  const isVoiceFile =
    isFileItem(item) &&
    isFileV2(item.file) &&
    item.file.recare_file_type === RECARE_FILE_TYPE.VOICE_SUMMARY;

  const filename = isAiCoreItem(item)
    ? item.doc.source === SOURCE_DOCUMENT_SOURCES.KIS_DATA
      ? translations.extractDocs.splitView.sourcesTab.kisDataCardTitle
      : item.doc.filename
    : item.name;
  const date = getUploadedAt(item);

  const sourceLabel = (() => {
    if (isAiCoreItem(item)) {
      return translations.extractDocs.splitView.sourcesTab.tabHis;
    }
    if (isVoiceFile) {
      return translations.patientForms.menu.recareVoiceLabel;
    }
    if (isFileHISStateful(item.file)) {
      return translations.extractDocs.splitView.sourcesTab.tabHis;
    }
    if (isFileFormUploaded(item.file)) {
      return translations.extractDocs.splitView.sourcesTab.createdWithRecare;
    }
    return translations.extractDocs.splitView.sourcesTab.tabUpload;
  })();

  const uploadedBy =
    isFileItem(item) && !isFileHISStateful(item.file) && !isVoiceFile
      ? getManuallyUploadedFileAccountName(item.file)
      : "";

  return (
    <div className="flex w-full flex-row items-center justify-start gap-4 px-6 py-4">
      <div className="flex items-center justify-center">
        {isLoading ? (
          <Spinner color="primary" size="sm" testId="spinner-loading-sources" />
        ) : isAiCoreItem(item) ? (
          <ListIcon className="h-6 w-6 text-primary" />
        ) : isVoiceFile ? (
          <MicIcon className="h-6 w-6 text-primary" />
        ) : (
          <FileTextIcon className="h-6 w-6 text-primary" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 text-left">
        <span className="text-body-md font-bold break-all text-gray-900">
          {filename}
        </span>
        <div className="flex flex-row items-center gap-1 text-body-sm text-gray-500">
          {date != null && <span>{formatUnixDate(date, locale)}</span>}
          {uploadedBy && (
            <>
              <span>•</span>
              <span>{uploadedBy}</span>
            </>
          )}
          <span>•</span>
          <span>{sourceLabel}</span>
        </div>
      </div>
      {isFileItem(item) && (
        <div className="flex items-center">
          <ExtractionStatusChip file={item.file} />
        </div>
      )}
    </div>
  );
};

const SourceFileCardDisplay = ({
  isInline = false,
  item,
}: {
  isInline?: boolean;
  item: SourceListItem;
}) => {
  if (isInline) {
    return (
      <div className="border-b border-gray-400 bg-white">
        <SourceItemCardBody item={item} />
      </div>
    );
  }
  return (
    <Card
      className="flex w-full flex-row rounded-lg border border-gray-300 bg-white"
      id={`source-display-card-${getSourceListItemTabKey(item)}`}
      shadow="none"
    >
      <SourceItemCardBody item={item} />
    </Card>
  );
};

const SourceFileViewer = ({
  contradictions,
  displayedTabDocuments,
  files,
  onSelectDoc,
  selectedDocument,
  selectedSource,
  value,
}: {
  files: { name: string; file: FileState }[];
  contradictions: Contradictions | undefined;
  displayedTabDocuments: DocsFormSourceDocument[];
  onSelectDoc: (file: DocsFormSourceDocument) => void;
  selectedDocument: DocsFormSourceDocument;
  selectedSource: SourceSelected<DocsFormName> | undefined;
  value: string | undefined;
}) => {
  const translations = useTranslations();
  const isRecareData =
    selectedDocument.source === SOURCE_DOCUMENT_SOURCES.RECARE_DATA;
  const isKisData =
    selectedDocument.source === SOURCE_DOCUMENT_SOURCES.KIS_DATA;
  const showKisContradictionValue =
    isKisData &&
    contradictions?.candidates?.length &&
    contradictions.status === CONTRADICTION_STATUS.CONFLICT;

  const selectedFile = getFileForSourceDocument(files, selectedDocument);

  const renderSourcePanel = (): ReactNode => {
    if (selectedDocument.is_deleted) {
      return (
        <div className="flex h-full w-full flex-1 flex-col">
          <Banner
            color="warning"
            icon={ShieldAlertIcon}
            message={
              translations.extractDocs.splitView.sourceInformation
                .sourceDeletedTooltip
            }
          />
          <div className="flex w-full flex-1 flex-col items-center justify-center bg-gray-50">
            <Image
              aria-hidden="true"
              className="h-[160px] w-auto overflow-visible"
              src={getStaticAsset("docs-deleted-source.svg")}
            />
            <p className="w-[50%] text-center text-heading-md text-gray-600">
              {
                translations.extractDocs.splitView.sourceInformation
                  .sourceDeletedDescription
              }
            </p>
          </div>
        </div>
      );
    }

    if (isRecareData) {
      return (
        <div className="flex h-full w-full flex-col gap-4">
          <div className="rounded-lg border border-gray-400 p-4">
            {value ? <p className="text-body-md font-bold">{value}</p> : null}
            <p className="text-body-md">
              {
                translations.extractDocs.splitView.sourcesTab
                  .sourcePatientProfile
              }
            </p>
          </div>
        </div>
      );
    }

    if (isKisData) {
      if (showKisContradictionValue) {
        return (
          <div className="flex h-full w-full flex-col gap-4">
            <div className="rounded-lg border border-gray-400 p-4">
              {value ? <p className="text-body-md font-bold">{value}</p> : null}
              <p className="text-body-md">
                {translations.extractDocs.splitView.sourcesTab.sourceHis}
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="flex h-full w-full flex-1 flex-col overflow-hidden rounded-t-lg border border-gray-400">
          {selectedDocument && (
            <SourceFileCardDisplay
              isInline
              item={{
                doc: {
                  document_id: selectedDocument.id,
                  filename: selectedDocument.filename,
                  source: selectedDocument.source,
                  source_document_uri: selectedDocument.url,
                  uploaded_at: selectedDocument.uploaded_at ?? 0,
                },
                kind: ITEM_KIND.AI_CORE,
              }}
            />
          )}
          <KisJsonViewer url={selectedDocument.url} />
        </div>
      );
    }

    const item = selectedFile
      ? {
          file: selectedFile.file,
          kind: isFileV2(selectedFile.file)
            ? ITEM_KIND.RECARE_FILE
            : ITEM_KIND.KIS_FILE,
          name: selectedDocument.filename,
        }
      : null;

    if (isDocxFileName(selectedDocument.filename)) {
      return (
        <div className="flex h-full w-full flex-1 flex-col overflow-hidden rounded-t-lg border border-gray-400">
          {item && <SourceFileCardDisplay isInline item={item} />}
          <DocxViewer
            className="rounded-none border-0 shadow-none"
            selectedFile={{
              fileName: selectedDocument.filename,
              id: selectedDocument.id,
              url: selectedDocument.url,
            }}
          />
        </div>
      );
    }

    return (
      <div className="flex h-full w-full flex-1 flex-col overflow-hidden rounded-t-lg border border-gray-400">
        {item && <SourceFileCardDisplay isInline item={item} />}
        <PdfViewer
          className="rounded-none border-0 shadow-none"
          regions={selectedDocument.regions}
          selectedFile={{
            fileName: selectedDocument.filename,
            id: selectedDocument.id,
            url: selectedDocument.url,
          }}
        />
      </div>
    );
  };

  const panelContent = (
    <div className="flex h-full w-full flex-1">{renderSourcePanel()}</div>
  );

  const sourceDocumentsForTabs = (() => {
    const base = displayedTabDocuments.length
      ? displayedTabDocuments
      : [selectedDocument];
    const hasSelected = base.some(
      (doc) => getDocumentTabKey(doc) === getDocumentTabKey(selectedDocument),
    );
    if (hasSelected) return base;
    return [...base];
  })();

  const displayedTabDocumentsSorted = sortSourceDocumentsForTabs(
    sourceDocumentsForTabs,
  );

  const tabItems: TabsProps["items"] = displayedTabDocumentsSorted.map(
    (tabDocument, index) => {
      const file = getFileForSourceDocument(files, tabDocument);

      const isSelected =
        getDocumentTabKey(selectedDocument) === getDocumentTabKey(tabDocument);

      const title = getTabTitle({
        contradictions,
        file: !!file,
        index,
        tabDocument,
        translations,
      });

      return {
        content: isSelected ? panelContent : null,
        key: getDocumentTabKey(tabDocument),
        title,
        titleText: title,
      };
    },
  );

  return (
    <div className="flex h-full w-full flex-col items-center gap-4 overflow-y-auto">
      <Header
        selectedDocument={selectedDocument}
        selectedSource={selectedSource}
        tabItems={tabItems}
        onSelectionChange={(tabKey: string) => {
          const sourceDocument = sourceDocumentsForTabs.find(
            (source) => getDocumentTabKey(source) === tabKey,
          );

          if (sourceDocument) onSelectDoc(sourceDocument);
        }}
      />
    </div>
  );
};

const SourceItemCard = ({
  isLoadingUrl,
  item,
  loadingFileId,
  onSelectDoc,
}: {
  item: SourceListItem;
  isLoadingUrl: boolean;
  loadingFileId: string | undefined;
  onSelectDoc: (doc: DocsFormSourceDocument) => void;
}) => {
  const translations = useTranslations();

  const isLoading =
    isLoadingUrl && loadingFileId === getSourceListItemTabKey(item);

  if (isAiCoreItem(item)) {
    const { doc } = item;
    return (
      <Card
        aria-label={translations.extractDocs.splitView.sourcesTab.ariaLabelFileCard(
          { fileName: doc.filename },
        )}
        as="button"
        className="flex w-full flex-row"
        id={`source-card-${doc.document_id}`}
        isLoading={isLoading}
        shadow="none"
        trackEventProps={{ file_id: doc.document_id, id: "source-card" }}
        onPress={() =>
          onSelectDoc({
            filename: doc.filename,
            id: doc.document_id,
            is_deleted: false,
            kis_file_id: null,
            recare_file_id: null,
            source: doc.source,
            url: doc.source_document_uri ?? "",
          })
        }
      >
        <SourceItemCardBody isLoading={isLoading} item={item} />
      </Card>
    );
  }

  const { file, name } = item;
  return (
    <Card
      aria-label={translations.extractDocs.splitView.sourcesTab.ariaLabelFileCard(
        { fileName: name },
      )}
      as="button"
      className="flex w-full flex-row"
      id={`source-card-${file.id}`}
      isLoading={isLoading}
      shadow="none"
      trackEventProps={{ file_id: String(file.id), id: "source-card" }}
      onPress={() =>
        onSelectDoc({
          filename: name,
          id: String(file.id),
          is_deleted: false,
          kis_file_id: isFileHISStateful(file) ? file.id : null,
          recare_file_id: !isFileHISStateful(file) ? file.id : null,
          source: isFileHISStateful(file)
            ? SOURCE_DOCUMENT_SOURCES.KIS_FILE
            : SOURCE_DOCUMENT_SOURCES.RECARE_FILE,
          url: "",
        })
      }
    >
      <SourceItemCardBody isLoading={isLoading} item={item} />
    </Card>
  );
};

const SourceFileList = ({
  files,
  isLoadingUrl,
  loadingFileId,
  onSelectDoc,
}: {
  files: { name: string; file: FileState }[];
  isLoadingUrl: boolean;
  loadingFileId: string | undefined;
  onSelectDoc: (file: DocsFormSourceDocument) => void;
}) => {
  const translations = useTranslations();
  const { extractOcrStatus } = useExtractDocs();

  const sortedItems = useMemo(() => {
    const documents = extractOcrStatus.result?.documents ?? [];

    const kisDataDocuments = [];

    for (const doc of documents) {
      if (doc.source === SOURCE_DOCUMENT_SOURCES.KIS_DATA) {
        kisDataDocuments.push(doc);
      }
    }

    return mergeAndSortSourceListItems(files, kisDataDocuments);
  }, [extractOcrStatus.result?.documents, files]);

  if (!sortedItems.length) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4">
        <Image
          alt={translations.extractDocs.splitView.sourcesTab.altTextEmptyState}
          className="h-[160px] w-auto overflow-visible"
          src={getStaticAsset("docs-empty-state.svg")}
        />
        <p className="w-[50%] text-center text-heading-md text-gray-600">
          {translations.extractDocs.splitView.sourcesTab.emptyStateInformation}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-lg border border-gray-300 bg-white">
      <ul className="flex h-fit w-full flex-col divide-y divide-gray-300">
        {sortedItems.map((item) => {
          const key = isAiCoreItem(item)
            ? `gateway-${item.doc.document_id}`
            : `file-${item.file.updated_at}-${item.file.id}`;
          return (
            <li key={key} className="w-full">
              <SourceItemCard
                isLoadingUrl={isLoadingUrl}
                item={item}
                loadingFileId={loadingFileId}
                onSelectDoc={onSelectDoc}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const SourceContent = ({
  externalId,
  files,
  formName,
  selectedDocument,
  selectedSource,
  setSelectedDocument,
}: {
  formName: DocsFormName;
  files: { name: string; file: FileState }[];
  externalId: string;
  selectedDocument: DocsFormSourceDocument | undefined;
  selectedSource: SourceSelected<typeof formName> | undefined;
  setSelectedDocument: (doc: DocsFormSourceDocument | undefined) => void;
}) => {
  const translations = useTranslations();
  const getOntology = useGetOntology();

  const [createFileUrlProgress, setCreateFileUrlProgress] =
    useState<QueryProgress>(QUERY_PROGRESS_NOT_STARTED);
  const urlCache = useRef<Map<string, string>>(new Map());
  const [loadingFileId, setLoadingFileId] = useState<string | undefined>();
  const { getValues } = useFormContext<DocsFormDataMapped<typeof formName>>();

  const selectSourceDocumentFile = useSelectSourceDocumentFile({
    canUseExtractedDocument: true,
    externalId,
    onComplete: () => setLoadingFileId(undefined),
    setProgress: setCreateFileUrlProgress,
  });

  const sourceDocuments = selectedSource?.sourceFields.source_documents ?? [];
  const contradictions = selectedSource?.contradictions;

  const sourceTabDisplayValue = useMemo(() => {
    const fieldName = selectedSource?.fieldName;
    const candidates = contradictions?.candidates;

    let displayValue: string | undefined;

    if (candidates?.length && selectedDocument) {
      const tabKey = getDocumentTabKey(selectedDocument);
      const forTab = candidates.find((c) =>
        c.source_documents.some((doc) => getDocumentTabKey(doc) === tabKey),
      );
      displayValue = forTab?.value;
    }

    // TODO: This is a hack to display the value of the selected source in the tab mocking the "recare_data" data.
    //  https://recare.atlassian.net/browse/DEV-19817;
    if (displayValue === undefined && fieldName != null) {
      displayValue = getValues(fieldName);
    }

    if (!fieldName) {
      return displayValue;
    }

    const formConfig = FORM_NAME_TO_CONTRADICTION_VALUE_CONFIG[formName];
    const contradictionValueConfig = formConfig[
      fieldName
    ] as ContradictionValueToHumanReadable<typeof formName>;

    if (!contradictionValueConfig) {
      return displayValue;
    }

    const { fieldLabel, valueLabel } = contradictionValueConfig({
      fieldName,
      getOntology,
      translations,
      value: displayValue as ValueOf<DocsFormDataMapped<typeof formName>>,
    });
    return `${fieldLabel}: ${valueLabel}`;
  }, [
    contradictions?.candidates,
    getOntology,
    getValues,
    selectedDocument,
    selectedSource?.fieldName,
    formName,
    translations,
  ]);

  const displayedTabDocuments = useMemo(() => {
    if (
      contradictions?.status === CONTRADICTION_STATUS.CONFLICT &&
      contradictions.candidates?.length
    ) {
      const contradictionDocs = contradictions.candidates.flatMap(
        (c) => c.source_documents ?? [],
      );
      const seen = new Set<string>();
      return contradictionDocs.filter((doc) => {
        const key = getDocumentTabKey(doc);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    return sourceDocuments ?? [];
  }, [contradictions, sourceDocuments]);

  const displayedTabDocumentsSorted = useMemo(
    () => sortSourceDocumentsForTabs(displayedTabDocuments),
    [displayedTabDocuments],
  );

  const handleSelectDoc = async (sourceDocument: DocsFormSourceDocument) => {
    setLoadingFileId(getDocumentTabKey(sourceDocument));
    const selectedSourceDocument = await selectSourceDocumentFile({
      files,
      sourceDocument,
      urlCache: urlCache.current,
    });
    setLoadingFileId(undefined);

    if (selectedSourceDocument) setSelectedDocument(selectedSourceDocument);
  };

  useEffect(() => {
    if (displayedTabDocumentsSorted.length) {
      handleSelectDoc(displayedTabDocumentsSorted[0]);
    }
  }, [displayedTabDocumentsSorted]);
  if (selectedDocument) {
    return (
      <SourceFileViewer
        contradictions={contradictions}
        displayedTabDocuments={displayedTabDocumentsSorted}
        files={files}
        selectedDocument={selectedDocument}
        selectedSource={selectedSource}
        value={sourceTabDisplayValue}
        onSelectDoc={handleSelectDoc}
      />
    );
  }

  return (
    <SourceFileList
      files={files}
      isLoadingUrl={isLoading(createFileUrlProgress)}
      loadingFileId={loadingFileId}
      onSelectDoc={handleSelectDoc}
    />
  );
};

export const DocsModalSources = ({
  files,
  formName,
  hasNewExtractedDocuments,
  onCloseNewExtractedDocumentsBanner,
  patient,
  resetSelectedSource,
  selectedSource,
}: {
  files: { name: string; file: FileState }[];
  formName: DocsFormName;
  hasNewExtractedDocuments: boolean;
  onCloseNewExtractedDocumentsBanner: () => void;
  patient: Patient;
  resetSelectedSource: () => void;
  selectedSource: SourceSelected<typeof formName> | undefined;
}) => {
  const translations = useTranslations();
  const getFieldLabel = useFieldLabel();
  const sourceFiles = useMemo(() => files.map(({ file }) => file), [files]);
  const { extractOcr } = useExtractDocs();
  const canExtractAllFiles = useCanExtractAllFiles(sourceFiles);

  const [selectedDocument, setSelectedDocument] = useState<
    DocsFormSourceDocument | undefined
  >();

  const sourceCount =
    selectedSource?.sourceFields.source_documents.length ?? files.length;

  const fieldLabel =
    selectedSource?.fieldName != null
      ? getFieldLabel(formName, selectedSource.fieldName)
      : undefined;

  const handleBack = () => {
    setSelectedDocument(undefined);
    resetSelectedSource();
  };

  return (
    <div className="flex h-full w-[50%] min-w-0 flex-col overflow-hidden border-l border-gray-400">
      <div className="sticky top-0 flex w-full flex-col gap-3 bg-white px-4 pt-4">
        {selectedDocument ? (
          <div className="flex min-w-0 items-center gap-3">
            <Button
              aria-label={translations.actions.back}
              className="shrink-0"
              color="primary"
              id="back"
              isIconOnly
              startContent={<ArrowLeftIcon />}
              variant="ghost"
              onPress={handleBack}
            />
            <h2 className="truncate text-heading-lg font-bold">
              {selectedSource != null && sourceCount > 1
                ? fieldLabel
                  ? translations.extractDocs.splitView.sourcesTab.titleWithCountAndField(
                      { count: String(sourceCount), field: fieldLabel },
                    )
                  : translations.extractDocs.splitView.sourcesTab.titleWithCount(
                      { count: String(sourceCount) },
                    )
                : fieldLabel
                ? translations.extractDocs.splitView.sourcesTab.titleWithField({
                    field: fieldLabel,
                  })
                : translations.extractDocs.splitView.sourcesTab.title}
            </h2>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-heading-lg font-bold">
              <FileSearch2Icon className="h-6 w-6 text-primary" />
              {translations.extractDocs.splitView.sourcesTab.titlePlural}
            </h2>
            {canExtractAllFiles && (
              <Button
                color="primary"
                id="extract-all-source-files"
                size="sm"
                startContent={<SparklesIcon />}
                onPress={() => {
                  extractOcr.handleExtractAll({
                    files: sourceFiles ?? [],
                  });
                }}
              >
                {translations.extractDocs.filesTabChanges.buttonExtractAll}
              </Button>
            )}
          </div>
        )}
        {hasNewExtractedDocuments && !selectedDocument && (
          <Banner
            color="warning"
            icon={ShieldAlertIcon}
            message={translations.extractDocs.splitView.header.bannerNewData}
            withClose
            onClose={onCloseNewExtractedDocumentsBanner}
          />
        )}
      </div>
      <div
        className={clsx("flex h-full w-full items-start overflow-y-auto p-4", {
          "bg-gray-50": !files.length,
        })}
      >
        <SourceContent
          externalId={patient.external_id!}
          files={files}
          formName={formName}
          selectedDocument={selectedDocument}
          selectedSource={selectedSource}
          setSelectedDocument={setSelectedDocument}
        />
      </div>
    </div>
  );
};
